import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as yup from 'yup';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Locale } from 'helpers/Enums';

interface ICustomerContext {
    isLoading: boolean
    customer: Customer

    searchCustomers: (searchText: string, etag: number) => Promise<any>
    saveCustomer: (customer: Customer) => void
}

export let customerSchema = yup.object().shape({
    userId: yup.string(),
    firstName: yup.string().default('').required().label("First name"),
    lastName: yup.string().default('').required().label("Last name"),
    email: yup.string().default('').required().label("Email"),
    isAdmin: yup.boolean().default(false).label("Is Admin"),
    language: yup.string().default(Locale.Italian).required().label('Language'),
    customerId: yup.number().default(0).nullable().label("Customer"),
    customerName: yup.string().default('').nullable().label("Customer Name"),
    countryCode: yup.string().default('').nullable().label("Country Code"),
    countryName: yup.string().default('').nullable().label("Country Name"),
    resetPassword: yup.boolean().default(false),
    newPassword: yup.string().default('')
        .when("resetPassword", {
            is: true,
            then: yup.string().default("").required("Please set new password"),
            otherwise: yup.string().default("")
        }),
    newPasswordConfirm: yup.string().default('')
        .when("resetPassword", {
            is: true,
            then: yup.string().default("").required("Please confirm new password"),
            otherwise: yup.string().default("")
        })
});

type Customer = yup.InferType<typeof customerSchema>;

export let customerContext = React.createContext({} as ICustomerContext);

let { Provider } = customerContext;

let CustomerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { id } = useParams<{ id: string }>();
    let location = useLocation();
    let history = useHistory();
    let { enqueueSnackbar } = useSnackbar();

    let isNew = location.pathname.includes("/addnew");

    let [isLoading, setLoading] = useState<boolean>(isNew === false);
    let [customer, setCustomer] = useState<Customer>(customerSchema.getDefault());


    useEffect(() => {
        if (isNew === false)
            getCustomer();

    }, [id, isNew])

    let getCustomer = async () => {
        try {
            let res = await axios.get('/api/Customer/GetCustomer', { params: { customerId: id } });

            let { item } = res.data;
            setCustomer({
                ...item,
                adminArea: item.adminArea ?? [],
                groups: item.groups ?? [],
                enabledLocales: item.enabledLocales ?? [],
                resetPassword: false,
                newPassword: '',
                newPasswordConfirm: ''
            });
            setLoading(false);

        } catch (err) { }
        finally {
        }
    }

    let saveCustomer = async (value: Customer) => {
        try {
            await axios.post('/api/Customer/SaveCustomer', value);
            enqueueSnackbar("Customer saved", { variant: "success" });
            history.push(`/customers`);

        } catch (err) {
        }
    }


    let searchCustomers = async (searchText: string, etag: number) => {
        if (!searchText)
            return;

        try {

            let request = {
                searchText: searchText,
                etag: etag
            };

            const res = await axios.post('/api/Order/CustomersQuickSearch', request);
            return res.data;
        } catch { }
    }

    return (
        <Provider
            value={{
                isLoading,
                customer,

                searchCustomers: searchCustomers,
                saveCustomer
            }}
        >
            {children}
        </Provider>
    )
}

export default CustomerProvider;